import {Flow} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Overlay} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Overlay';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {FocusedArea} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/WorkbenchState';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons} from '@mintdata-wsp/resources/icons/WSPIcons';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {Shortcuts} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/KeyboardController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IContextMenuItem} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import * as classnames from 'classnames/bind';
import * as React from 'react';

const styles = require('./TopMenu.less');
const cx = classnames.bind(styles);

export abstract class EditMenuItems
{
    public static build():IContextMenuItem[]
    {
        const {surfaceArea, spreadsheets, undoRedo, keyboard} = controllers.ui;

        const surfaceState:SurfaceState = surfaceArea.getState();

        if( !surfaceState )
            return null;

        const activePage:Page = surfaceArea.getActivePage();
        const activePageName:string = activePage ? activePage.name : '';

        const overlays:Overlay[] = activePage && activePage.overlays;
        const pagesForOverlay:Page[] = surfaceState.pagesItemsOrdered.filter((page:Page) => page.id !== activePage.id);

        const activeSheet:AnySheet = spreadsheets.main.getActiveSheet();
        const activeSheetName:string = activeSheet ? activeSheet.name : '';

        const flows:Flow[] = controllers.ui.flow.getFlows();

        const focusedArea:FocusedArea = controllers.ui.workbench.getFocusedArea();
        const selectedBoxes = controllers.ui.surfaceSelection.getSelectedBoxes();
        const areMultipleBoxesSelected = selectedBoxes.length >= 1;
        const canBoxBeUngrouped = selectedBoxes.length === 1 && selectedBoxes[0].children.length > 0;

        const isUndoDisabled = !undoRedo.isUndoAllowed;
        const isRedoDisabled = !undoRedo.isRedoAllowed;

        return [
            {
                label: 'Undo',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.UNDO),
                isDisabled: isUndoDisabled,
                icon: <WSPIcon className={cx('icon')} name={WSPIcons.visualDesigner.menu.Undo} />,
                handler: controllers.ui.topMenu.undoHandler
            },
            {
                label: 'Redo',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.REDO),
                isDisabled: isRedoDisabled,
                icon: <WSPIcon className={cx('icon')} name={WSPIcons.visualDesigner.menu.Redo} />,
                handler: controllers.ui.topMenu.redoHandler
            },
            {
                isSeparator: true
            },
            ...this._buildCutCopyPasteMenuItems(),
            {
                isSeparator: true
            },
            focusedArea === FocusedArea.Surface ? {
                label: 'Group selected',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.SURFACE_GROUP_INTO_BOX),
                isDisabled: !areMultipleBoxesSelected,
                handler: controllers.ui.topMenu.surfaceGroupHandler,
            } : undefined,
            focusedArea === FocusedArea.Surface ? {
                label: 'Ungroup selected',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.SURFACE_UNGROUP_FROM_BOX),
                isDisabled: !canBoxBeUngrouped,
                handler: controllers.ui.topMenu.surfaceUngroupHandler,
            } : undefined,
            focusedArea === FocusedArea.Surface ? {
                isSeparator: true
            } : undefined,
            {
                label: 'New page',
                handler: controllers.ui.topMenu.newPageHandler
            },
            {
                label: <span><b>{activePageName}</b></span>,
                dataSource: this.buildPagesMenuItems()
            },
            {
                isSeparator: true
            },
            {
                label: <span>Overlays on <b>{activePageName}</b></span>,
                dataSource: this.buildOverlaysMenuItems(),
                isDisabled: pagesForOverlay.length === 0
            },
            {
                label: <span>Pinned Overlays on <b>{activePageName}</b></span>,
                dataSource: this.buildPinnedOverlaysMenuItems(),
                isDisabled: !overlays || overlays.length === 0
            },
            {
                isSeparator: true
            },
            {
                label: 'New sheet',
                handler: controllers.ui.topMenu.newSheetHandler
            },
            {
                label: <span><b>{activeSheetName}</b></span>,
                dataSource: this.buildSheetsMenuItems()
            },
            {
                isSeparator: true
            },
            {
                label: 'New cloud flow',
                handler: controllers.ui.topMenu.newCloudFlowHandler
            },
            {
                label: 'View cloud flow',
                dataSource: this.buildCloudFlowsMenuItems(),
                isDisabled: flows.length === 0
            }
        ].filter(item => item);
    }

    private static _buildCutCopyPasteMenuItems():IContextMenuItem[]
    {
        const focusedArea:FocusedArea = controllers.ui.workbench.getFocusedArea();

        const cutItem:IContextMenuItem = focusedArea === FocusedArea.Surface
            ? this.buildSurfaceCutMenuItem(true)
            : this.buildSpreadsheetCutMenuItem(true);

        const copyItem:IContextMenuItem = focusedArea === FocusedArea.Surface
            ? this.buildSurfaceCopyMenuItem(true)
            : this.buildSpreadsheetCopyMenuItem(true);

        const pasteItem:IContextMenuItem = focusedArea === FocusedArea.Surface
            ? this.buildSurfacePasteMenuItem(true)
            : this.buildSpreadsheetPasteMenuItem(true);

        const pasteValuesOnlyItem:IContextMenuItem = this.buildSpreadsheetPasteValuesOnlyMenuItem();

        if( focusedArea === FocusedArea.None )
        {
            cutItem.isDisabled = true;
            copyItem.isDisabled = true;
            pasteItem.isDisabled = true;
            pasteValuesOnlyItem.isDisabled = true;
        }

        if( focusedArea === FocusedArea.Surface )
            pasteValuesOnlyItem.isDisabled = true;

        return [
            cutItem,
            copyItem,
            pasteItem,
            pasteValuesOnlyItem
        ];
    }

    public static buildSurfaceCutMenuItem(withContext:boolean = false):IContextMenuItem
    {
        const isDisabled:boolean = true;

        let label:string = 'Cut';

        if( withContext )
        {
            const selectedBoxes:Box[] = controllers.ui.surfaceSelection.getSelectedBoxes();
            label = `Cut ${selectedBoxes.length === 1 ? 'component' : 'components'}`;
        }

        return {
            label,
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.CUT),
            icon: <WSPIcon className={cx('icon', {isDisabled})} name={WSPIcons.visualDesigner.menu.Cut} />,
            handler: controllers.ui.topMenu.surfaceCutHandler,
            isDisabled
        };
    }

    public static buildSurfaceCopyMenuItem(withContext:boolean = false):IContextMenuItem
    {
        const isDisabled:boolean = !controllers.ui.surfaceArea.isCopyActionAvailable();

        let label:string = 'Copy';

        if( withContext )
        {
            const selectedBoxes:Box[] = controllers.ui.surfaceSelection.getSelectedBoxes();
            label = `Copy ${selectedBoxes.length === 1 ? 'component' : 'components'}`;
        }

        return {
            label,
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.COPY),
            icon: <WSPIcon className={cx('icon', {isDisabled})} name={WSPIcons.visualDesigner.menu.Copy} />,
            handler: controllers.ui.topMenu.surfaceCopyHandler,
            isDisabled
        };
    }

    public static buildSurfacePasteMenuItem(withContext:boolean = false):IContextMenuItem
    {
        const isDisabled:boolean = !controllers.ui.surfaceArea.isPasteActionAvailable();

        let label:string = 'Paste';

        if( withContext )
        {
            const selectedBoxes:Box[] = controllers.ui.surfaceSelection.getSelectedBoxes();
            label = `Paste ${selectedBoxes.length === 1 ? 'component' : 'components'}`;
        }

        return {
            label,
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.PASTE),
            icon: <WSPIcon className={cx('icon', {isDisabled})} name={WSPIcons.visualDesigner.menu.Paste} />,
            handler: controllers.ui.topMenu.surfacePasteHandler,
            isDisabled
        };
    }

    public static buildSpreadsheetCutMenuItem(withContext:boolean = false):IContextMenuItem
    {
        const {main, workbook, mesh} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(main.getActiveSheet().id);
        const meshState = mesh.getState(ctx);
        const isDisabled:boolean = main.isEditMode || meshState.details.isEditNoteMode;

        let label:string = 'Cut';

        if( withContext )
        {
            const selectedRange:MeshRange = meshState.details.selectedRange;
            label = `Cut ${selectedRange.isSingleCell ? 'cell' : 'cells'}`;
        }

        return {
            label,
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.CUT),
            icon: <WSPIcon className={cx('icon', {isDisabled})} name={WSPIcons.visualDesigner.menu.Cut} />,
            handler: controllers.ui.topMenu.spreadsheetCutHandler
        };
    }

    public static buildSpreadsheetCopyMenuItem(withContext:boolean = false):IContextMenuItem
    {
        const {main, workbook, mesh} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(main.getActiveSheet().id);
        const meshState = mesh.getState(ctx);
        const isDisabled:boolean = main.isEditMode || meshState.details.isEditNoteMode;

        let label:string = 'Copy';

        if( withContext )
        {
            const selectedRange:MeshRange = meshState.details.selectedRange;
            label = `Copy ${selectedRange.isSingleCell ? 'cell' : 'cells'}`;
        }

        return {
            label,
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.COPY),
            icon: <WSPIcon className={cx('icon', {isDisabled})} name={WSPIcons.visualDesigner.menu.Copy} />,
            handler: controllers.ui.topMenu.spreadsheetCopyHandler,
            isDisabled
        };
    }

    public static buildSpreadsheetPasteMenuItem(withContext:boolean = false):IContextMenuItem
    {
        const {main, workbook, mesh} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(main.getActiveSheet().id);
        const meshState = mesh.getState(ctx);

        let label:string = 'Paste';

        if( withContext )
        {
            const selectedRange:MeshRange = meshState.details.selectedRange;
            label = `Paste ${selectedRange.isSingleCell ? 'cell' : 'cells'}`;
        }

        return {
            label,
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.PASTE),
            icon: <WSPIcon className={cx('icon')} name={WSPIcons.visualDesigner.menu.Paste} />,
            handler: controllers.ui.topMenu.spreadsheetPasteHandler
        };
    }

    public static buildSpreadsheetPasteValuesOnlyMenuItem():IContextMenuItem
    {
        return {
            label: <span>Paste <b>values</b> only</span>,
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.SPREADSHEET_PASTE_AS_PLAIN_TEXT),
            handler: controllers.ui.topMenu.spreadsheetPasteValuesOnlyHandler
        };
    }

    public static buildPagesMenuItems():IContextMenuItem[]
    {
        const {surfaceArea, topMenu} = controllers.ui;
        const activePage:Page = surfaceArea.getActivePage();

        return [
            {
                label: <span>Duplicate</span>,
                handler: topMenu.duplicatePageHandler
            },
            {
                label: <span>Rename</span>,
                handler: topMenu.renamePageHandler
            },
            {
                isSeparator: true
            },
            {
                label: <span>Mark as starting page</span>,
                handler: topMenu.markAsStartingPageHandler,
                isDisabled: activePage && activePage.id === surfaceArea.getState().startPageId
            },
            {
                isSeparator: true
            },
            {
                label: <span>Delete</span>,
                handler: topMenu.deletePageHandler,
                isDisabled: surfaceArea.getState() && !surfaceArea.isPageRemoveAvailable()
            }
        ];
    }

    public static buildOverlaysMenuItems():IContextMenuItem[]
    {
        const {surfaceArea} = controllers.ui;

        const surfaceState:SurfaceState = surfaceArea.getState();
        const activePage:Page = surfaceArea.getActivePage();

        const overlays:Overlay[] = activePage.overlays;
        const pagesForOverlay:Page[] = surfaceState.pagesItemsOrdered.filter((page:Page) => page.id !== activePage.id);

        return pagesForOverlay.map((page:Page) =>
        {
            const overlay:Overlay = overlays.find((overlay:Overlay) => overlay.page.id === page.id);
            const isSelected:boolean = !!overlay;

            return {
                label: page.name,
                isSelected,
                handler: () =>
                {
                    if( isSelected )
                        surfaceArea.removeOverlay(overlay);
                    else
                        surfaceArea.showOverlay(page.id);
                }
            };
        });
    }

    public static buildPinnedOverlaysMenuItems():IContextMenuItem[]
    {
        const {surfaceArea} = controllers.ui;

        const activePage:Page = surfaceArea.getActivePage();

        const overlays:Overlay[] = activePage.overlays;
        const pinnedOverlays:Overlay[] = activePage.overlays.filter((overlay:Overlay) => overlay.isPinned);

        return overlays.map((overlay:Overlay) =>
        {
            const isSelected:boolean = !!pinnedOverlays.find((item:Overlay) => item.page.id === overlay.page.id);

            return {
                label: overlay.page.name,
                isSelected,
                handler: () => overlay.setIsPinned(!isSelected)
            };
        });
    }

    public static buildSheetsMenuItems():IContextMenuItem[]
    {
        const {topMenu} = controllers.ui;
        const {workbook, main} = controllers.ui.spreadsheets;

        const activeSheet:AnySheet = main.getActiveSheet();

        const workbookId:WorkbookId = main.getRootWorkbook().id;
        const numSheets:number = workbook.getNumSheets(workbookId);
        const sheetIndex:number = workbook.findSheetIndex(workbookId, activeSheet.id);

        const isFirstSheet:boolean = sheetIndex === 0;
        const isLastSheet:boolean = (sheetIndex === numSheets - 1);
        const isSingleSheet:boolean = numSheets === 1;

        return [
            {
                label: <span>Duplicate</span>,
                handler: topMenu.duplicateSheetHandler
            },
            {
                label: <span>Rename</span>,
                handler: topMenu.renameSheetHandler
            },
            {
                isSeparator: true
            },
            {
                label: <span>Move left</span>,
                handler: topMenu.moveLeftSheetHandler,
                isDisabled: isFirstSheet
            },
            {
                label: <span>Move right</span>,
                handler: topMenu.moveRightSheetHandler,
                isDisabled: isLastSheet
            },
            {
                isSeparator: true
            },
            {
                label: <span>Delete</span>,
                handler: topMenu.deleteSheetHandler,
                isDisabled: isSingleSheet
            }
        ];
    }

    public static buildCloudFlowsMenuItems():IContextMenuItem[]
    {
        const {projectId, designId} = controllers.ui.openDesign.getOpenDesign().designRef;

        const flows:Flow[] = controllers.ui.flow.getFlows();

        return flows.map((flow:Flow) =>
        {
            return {
                label: flow.name,
                handler: () => controllers.navigation.toFlowEditor(projectId, designId, flow.id)
            };
        });
    }
}
